import type { SheetData, SheetRow } from "./models";

/**
 * Returns a flattened array of only the visible rows in the spreadsheet,
 * by traversing the tree structure and respecting the `isExpanded` state of parent rows.
 *
 * We assume `sheet.rows` is pre-sorted in the order they should appear,
 * we just need to filter out rows whose ancestors are collapsed.
 */
export function getVisibleRows(sheet: SheetData): SheetRow[] {
    const visibleRows: SheetRow[] = [];

    // A Set to keep track of collapsed parent IDs for quick lookup O(1)
    const collapsedParentIds = new Set<string | number>();

    // Since rows are sequential (parent -> children -> grandchildren),
    // we can easily determine visibility by checking if a row's parent is in the collapsed set.
    for (const row of sheet.rows) {
        let isVisible = true;

        if (row.parentId !== undefined) {
            // If this row's parent is collapsed, this row is NOT visible
            if (collapsedParentIds.has(row.parentId)) {
                isVisible = false;
                // As long as this row is hidden, its children are also recursively hidden,
                // so we add *it* to the collapsed set for subsequent rows
                collapsedParentIds.add(row.rowId);
            }
        }

        if (isVisible) {
            visibleRows.push(row);

            // If the row IS visible, but it itself is collapsed, all its *direct* children
            // will now be hidden
            if (row.isExpanded === false) {
                collapsedParentIds.add(row.rowId);
            }
        }
    }

    return visibleRows;
}
